import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import PlayArrowOutlinedIcon from "@mui/icons-material/PlayArrowOutlined";
import { classMakeupApi } from "./api";
import { useConfirm } from "../../Components/Confirm";
import type {
  ImportItem,
  MakeupFilters,
  MakeupRecord,
  MakeupSummary,
} from "./types";
import MakeupPreviewDrawer from "./MakeupPreviewDrawer";
import { formatDate, formatTime, statusChip } from "./ui";

const todayStr = () => new Date().toISOString().slice(0, 10);

export default function ClassMakeupImportPage() {
  // Draft (form) vs applied filters — mirrors ClassBackupImport page.
  const [fromDraft, setFromDraft] = useState<string>(todayStr());
  const [toDraft, setToDraft] = useState<string>("");
  const [searchDraft, setSearchDraft] = useState<string>("");
  const [statusDraft, setStatusDraft] = useState<string>("all");
  const [hasMakeupIdDraft, setHasMakeupIdDraft] = useState<string>("any");
  const [importableOnlyDraft, setImportableOnlyDraft] = useState<boolean>(false);
  const [unresolvedOnlyDraft, setUnresolvedOnlyDraft] = useState<boolean>(false);

  const [filters, setFilters] = useState<MakeupFilters>({ from_date: todayStr() });

  const [items, setItems] = useState<MakeupRecord[]>([]);
  const [summary, setSummary] = useState<MakeupSummary | null>(null);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(25);
  const [total, setTotal] = useState(0);

  const [selected, setSelected] = useState<Record<string, true>>({});
  const [selectAllFiltered, setSelectAllFiltered] = useState(false);
  const [excludedKeys, setExcludedKeys] = useState<Record<string, true>>({});
  const [previewKey, setPreviewKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState<null | { total: number; done: number; results: ImportItem[] }>(null);

  const listReqRef = useRef(0);

  const loadSummary = useCallback(async () => {
    setLoadingSummary(true);
    try {
      const s = await classMakeupApi.summary(filters);
      setSummary(s);
    } catch (e: any) {
      setError(e.message || String(e));
    } finally {
      setLoadingSummary(false);
    }
  }, [filters]);

  const loadList = useCallback(async () => {
    const token = ++listReqRef.current;
    setLoadingList(true);
    try {
      const r = await classMakeupApi.list({ ...filters, page: page + 1, per_page: perPage });
      if (token !== listReqRef.current) return;
      setItems(r.items);
      setTotal(r.total);
    } catch (e: any) {
      if (token === listReqRef.current) setError(e.message || String(e));
    } finally {
      if (token === listReqRef.current) setLoadingList(false);
    }
  }, [filters, page, perPage]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadList(); }, [loadList]);

  const applyFilters = () => {
    const next: MakeupFilters = {
      from_date: fromDraft || undefined,
      to_date: toDraft || undefined,
      search: searchDraft || undefined,
      status: statusDraft !== "all" ? statusDraft : undefined,
      has_makeup_id:
        hasMakeupIdDraft === "yes" ? true : hasMakeupIdDraft === "no" ? false : undefined,
      importable_only: importableOnlyDraft || undefined,
      unresolved_only: unresolvedOnlyDraft || undefined,
    };
    setFilters(next);
    setPage(0);
    setSelected({});
    setSelectAllFiltered(false);
    setExcludedKeys({});
  };

  const refresh = () => { loadSummary(); loadList(); };

  const selectionCount = useMemo(() => {
    if (selectAllFiltered) {
      const excluded = Object.keys(excludedKeys).length;
      return Math.max(0, (summary?.total ?? total) - excluded);
    }
    return Object.keys(selected).length;
  }, [selectAllFiltered, excludedKeys, selected, summary?.total, total]);

  const toggleOne = (key: string) => {
    if (selectAllFiltered) {
      setExcludedKeys((prev) => {
        const next = { ...prev };
        if (next[key]) delete next[key]; else next[key] = true;
        return next;
      });
    } else {
      setSelected((prev) => {
        const next = { ...prev };
        if (next[key]) delete next[key]; else next[key] = true;
        return next;
      });
    }
  };

  const togglePage = () => {
    const allChecked = items.every((i) => (selectAllFiltered ? !excludedKeys[i.record_key] : !!selected[i.record_key]));
    if (selectAllFiltered) {
      setExcludedKeys((prev) => {
        const next = { ...prev };
        items.forEach((i) => {
          if (allChecked) next[i.record_key] = true;
          else delete next[i.record_key];
        });
        return next;
      });
    } else {
      setSelected((prev) => {
        const next = { ...prev };
        items.forEach((i) => {
          if (allChecked) delete next[i.record_key];
          else next[i.record_key] = true;
        });
        return next;
      });
    }
  };

  const clearSelection = () => {
    setSelected({});
    setExcludedKeys({});
    setSelectAllFiltered(false);
  };

  const confirm = useConfirm();
  const runImport = async (payload: Parameters<typeof classMakeupApi.importSelected>[0], count: number) => {
    if (count === 0) return;
    const ok = await confirm({
      title: "Import makeup classes?",
      message: `Import ${count} makeup class(es)? This creates one occurrence per record.`,
      confirmText: "Import",
      cancelText: "Cancel",
      variant: "default",
    });
    if (!ok) return;
    setError(null);
    setProcessing({ total: count, done: 0, results: [] });
    try {
      const res = await classMakeupApi.importSelected(payload);
      setProcessing({ total: count, done: count, results: res.items || [] });
      refresh();
      clearSelection();
    } catch (e: any) {
      setError(e.message || String(e));
    }
  };

  const importSelected = () => {
    if (selectAllFiltered) {
      runImport({
        selection_mode: "all_filtered",
        excluded_keys: Object.keys(excludedKeys),
        filters,
      }, selectionCount);
    } else {
      const keys = Object.keys(selected);
      runImport({
        selection_mode: "selected",
        record_keys: keys,
        filters,
      }, keys.length);
    }
  };

  const importAllReady = () => {
    runImport({
      selection_mode: "all_filtered",
      excluded_keys: [],
      filters: { ...filters, importable_only: true },
    }, summary?.ready ?? 0);
  };

  const importOneRow = async (key: string) => {
    try {
      const res = await classMakeupApi.importOne(key, filters.from_date, filters.to_date);
      refresh();
      if (res.status !== "imported" && res.status !== "already_imported") {
        setError(`Import result: ${res.status}${res.error ? ` — ${res.error}` : ""}`);
      }
    } catch (e: any) {
      setError(e.message || String(e));
    }
  };

  const cardValue = (v: number | undefined) => v ?? "–";

  return (
    <Box p={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1}>
        <Box>
          <Typography variant="h5" fontWeight={600}>Makeup Classes Import</Typography>
          <Typography variant="body2" color="text.secondary">
            Track and import makeup sessions referenced from the legacy day-details records.
          </Typography>
        </Box>
        <IconButton onClick={refresh} title="Refresh" disabled={loadingList || loadingSummary}>
          <RefreshOutlinedIcon />
        </IconButton>
      </Stack>

      <Alert severity="warning" sx={{ mb: 2 }}>
        Makeup imports are one-off sessions. They will not modify or regenerate regular recurring classes.
      </Alert>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Grid container spacing={1.5} mb={2}>
        {[
          { label: "Total References", value: summary?.total },
          { label: "Ready", value: summary?.ready },
          { label: "Already Imported", value: summary?.already_imported },
          { label: "Missing Makeup Day", value: summary?.missing_makeup_day },
          { label: "Unmapped Students", value: summary?.unmapped_student },
          { label: "Unmapped Staff", value: summary?.unmapped_staff },
          { label: "Missing Grade/Subject", value: summary?.missing_grade_subject },
          { label: "Conflicts", value: summary?.conflict },
          { label: "Failed Imports", value: summary?.failed_imports },
        ].map((c) => (
          <Grid key={c.label} item xs={6} sm={4} md={2}>
            <Card variant="outlined"><CardContent sx={{ py: 1.25 }}>
              <Typography variant="caption" color="text.secondary">{c.label}</Typography>
              <Typography variant="h6">{cardValue(c.value)}</Typography>
            </CardContent></Card>
          </Grid>
        ))}
      </Grid>

      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} mb={2} flexWrap="wrap">
        <TextField label="From (makeup date)" type="date" size="small" value={fromDraft}
          onChange={(e) => setFromDraft(e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField label="To (makeup date)" type="date" size="small" value={toDraft}
          onChange={(e) => setToDraft(e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField label="Search" size="small" value={searchDraft}
          onChange={(e) => setSearchDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") applyFilters(); }}
          placeholder="Student, staff, email, makeup day ID" sx={{ minWidth: 240 }} />
        <TextField label="Status" size="small" select value={statusDraft}
          onChange={(e) => setStatusDraft(e.target.value)} sx={{ minWidth: 200 }}>
          <MenuItem value="all">All</MenuItem>
          <MenuItem value="ready">Ready</MenuItem>
          <MenuItem value="already_imported">Already imported</MenuItem>
          <MenuItem value="unmapped_student">Unmapped student</MenuItem>
          <MenuItem value="unmapped_staff">Unmapped staff</MenuItem>
          <MenuItem value="missing_makeup_id">Missing makeup ID</MenuItem>
          <MenuItem value="missing_makeup_day">Missing makeup day</MenuItem>
          <MenuItem value="missing_grade_subject">Missing grade/subject</MenuItem>
          <MenuItem value="invalid_time">Invalid time</MenuItem>
          <MenuItem value="invalid_date">Invalid date</MenuItem>
          <MenuItem value="cancelled">Cancelled</MenuItem>
          <MenuItem value="released">Released</MenuItem>
          <MenuItem value="conflict">Conflict</MenuItem>
        </TextField>
        <TextField label="Has makeup_id" size="small" select value={hasMakeupIdDraft}
          onChange={(e) => setHasMakeupIdDraft(e.target.value)} sx={{ minWidth: 160 }}>
          <MenuItem value="any">Any</MenuItem>
          <MenuItem value="yes">Yes</MenuItem>
          <MenuItem value="no">No</MenuItem>
        </TextField>
        <Stack direction="row" spacing={1} alignItems="center">
          <label style={{ fontSize: 13 }}>
            <Checkbox size="small" checked={importableOnlyDraft} onChange={(e) => setImportableOnlyDraft(e.target.checked)} />
            Importable only
          </label>
          <label style={{ fontSize: 13 }}>
            <Checkbox size="small" checked={unresolvedOnlyDraft} onChange={(e) => setUnresolvedOnlyDraft(e.target.checked)} />
            Unresolved only
          </label>
        </Stack>
        <Button variant="outlined" onClick={applyFilters} disabled={loadingList}>Apply</Button>
      </Stack>

      <Stack direction="row" spacing={1} mb={1} alignItems="center" flexWrap="wrap">
        <Button size="small" onClick={() => {
          setSelectAllFiltered(true);
          setSelected({});
          setExcludedKeys({});
        }}>
          Select all {summary?.total ?? total} matching
        </Button>
        <Button size="small" onClick={clearSelection}>Clear selection</Button>
        <Box flex={1} />
        <Typography variant="body2" color="text.secondary">
          {selectAllFiltered
            ? `All ${selectionCount} matching selected`
            : `${selectionCount} selected on this page`}
        </Typography>
        <Button size="small" variant="outlined" onClick={importSelected}
          disabled={selectionCount === 0 || !!processing}>
          Import Selected
        </Button>
        <Button size="small" variant="contained" color="primary" onClick={importAllReady}
          disabled={!(summary?.ready ?? 0) || !!processing}>
          Import All Ready ({summary?.ready ?? 0})
        </Button>
      </Stack>

      {processing && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setProcessing(null)}>
          {processing.done < processing.total
            ? `Processing ${processing.done}/${processing.total}…`
            : `Done: ${processing.results.filter(r => r.status === "imported").length} imported, ${processing.results.filter(r => r.status === "already_imported").length} already imported, ${processing.results.filter(r => r.status === "failed").length} failed.`}
        </Alert>
      )}

      <TableContainer component={Card} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  size="small"
                  indeterminate={
                    items.some((i) =>
                      selectAllFiltered ? !excludedKeys[i.record_key] : !!selected[i.record_key]
                    ) &&
                    !items.every((i) =>
                      selectAllFiltered ? !excludedKeys[i.record_key] : !!selected[i.record_key]
                    )
                  }
                  checked={
                    items.length > 0 &&
                    items.every((i) =>
                      selectAllFiltered ? !excludedKeys[i.record_key] : !!selected[i.record_key]
                    )
                  }
                  onChange={togglePage}
                />
              </TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Student</TableCell>
              <TableCell>Grade / Subject</TableCell>
              <TableCell>Original</TableCell>
              <TableCell>Makeup</TableCell>
              <TableCell>Staff (Makeup)</TableCell>
              <TableCell>Location</TableCell>
              <TableCell align="center">Legacy IDs</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loadingList && (
              <TableRow><TableCell colSpan={10} align="center"><CircularProgress size={20} /></TableCell></TableRow>
            )}
            {!loadingList && items.length === 0 && (
              <TableRow><TableCell colSpan={10} align="center">No matching makeup records.</TableCell></TableRow>
            )}
            {!loadingList && items.map((i) => {
              const rowChecked = selectAllFiltered
                ? !excludedKeys[i.record_key]
                : !!selected[i.record_key];
              const readyOrImported =
                i.status === "ready" || i.status === "already_imported";
              return (
                <TableRow key={i.record_key} hover>
                  <TableCell padding="checkbox">
                    <Checkbox size="small" checked={rowChecked} onChange={() => toggleOne(i.record_key)} />
                  </TableCell>
                  <TableCell>
                    {statusChip(i.status)}
                    {i.warnings.length > 0 && (
                      <Tooltip title={<Box>{i.warnings.map((w, idx) => <div key={idx}>• {w}</div>)}</Box>}>
                        <Chip size="small" label={`${i.warnings.length} warn`} sx={{ ml: 0.5 }} />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{i.student_name || "—"}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      legacy #{i.legacy_student_id}{i.live_student_id ? ` → live #${i.live_student_id}` : ""}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {i.grade_id ? `Grade ${i.grade_id}` : "—"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {i.student_subject_ids || i.original_grade_subject || "—"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{formatDate(i.original_date)}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatTime(i.original_from_time)}–{formatTime(i.original_to_time)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {i.makeup_row_missing ? (
                      <Chip size="small" color="error" label="Missing" />
                    ) : (
                      <>
                        <Typography variant="body2">{formatDate(i.makeup_date)}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatTime(i.makeup_start_time)}–{formatTime(i.makeup_end_time)}
                        </Typography>
                      </>
                    )}
                  </TableCell>
                  <TableCell>
                    {i.makeup_staff_name ? (
                      <>
                        <Typography variant="body2">{i.makeup_staff_name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {i.live_makeup_staff_id ? `live #${i.live_makeup_staff_id}` : "unmapped"}
                        </Typography>
                      </>
                    ) : "—"}
                  </TableCell>
                  <TableCell>{i.location_name || <Chip size="small" label="Unassigned" />}</TableCell>
                  <TableCell align="center">
                    <Typography variant="caption" display="block">o#{i.legacy_original_day_id}</Typography>
                    <Typography variant="caption" display="block">m#{i.legacy_makeup_day_id || "—"}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Preview">
                      <IconButton size="small" onClick={() => setPreviewKey(i.record_key)}>
                        <VisibilityOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {i.status === "ready" && (
                      <Tooltip title="Import this record">
                        <IconButton size="small" color="primary" onClick={() => importOneRow(i.record_key)}>
                          <PlayArrowOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={total}
        page={page}
        rowsPerPage={perPage}
        rowsPerPageOptions={[25, 50, 100]}
        onPageChange={(_, p) => setPage(p)}
        onRowsPerPageChange={(e) => { setPerPage(parseInt(e.target.value, 10)); setPage(0); }}
      />

      <MakeupPreviewDrawer
        recordKey={previewKey}
        fromDate={filters.from_date}
        toDate={filters.to_date}
        onClose={() => setPreviewKey(null)}
      />
    </Box>
  );
}
